import React, { useState, useEffect } from 'react';
import {
    Card,
    Avatar,
    Typography,
    Button,
    Space,
    Badge,
    Tooltip
} from 'antd';
import {
    MessageOutlined,
    CloseOutlined,
    UserOutlined,
    PhoneOutlined,
    CalendarOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import pusherService from '../../services/pusher';
import dayjs from 'dayjs';

const { Text: AntText, Title } = Typography;

function MessageNotification() {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (!user) return;

        // Set up Pusher notifications for new messages
        const setupNotifications = async () => {
            try {
                // Listen for new messages on user channel
                const userChannel = await pusherService.subscribeToUser(user.id);
                
                if (userChannel) {
                    userChannel.bind('new-message', (data) => {
                        console.log('New message notification received:', data);
                        showNotification(data);
                    });
                }

                // Listen for new leads if user is vendor
                if (user.user_type === 'vendor' && user.vendor?.id) {
                    const leadChannel = await pusherService.subscribeToChannel('new-leads');
                    if (leadChannel) {
                        leadChannel.bind('new-lead', (data) => {
                            console.log('New lead notification received:', data);
                            showLeadNotification(data);
                        });
                    }
                }
            } catch (error) {
                console.error('Failed to setup message notifications:', error);
            }
        };

        setupNotifications();
    }, [user]);

    const showNotification = (data) => {
        const notification = {
            id: Date.now(),
            type: 'message',
            title: 'New Message',
            message: data.message?.message || 'You have a new message',
            sender: data.message?.user?.name || 'Unknown',
            leadId: data.message?.lead_id,
            timestamp: new Date(),
            avatar: data.message?.user?.avatar
        };

        setNotifications(prev => [notification, ...prev.slice(0, 4)]); // Keep only last 5
        setIsVisible(true);

        // Auto-hide after 5 seconds
        setTimeout(() => {
            setIsVisible(false);
        }, 5000);
    };

    const showLeadNotification = (data) => {
        const notification = {
            id: Date.now(),
            type: 'lead',
            title: 'New Booking Request',
            message: `${data.lead?.user?.name} has requested catering for ${data.lead?.event_type}`,
            sender: data.lead?.user?.name || 'Unknown',
            leadId: data.lead?.id,
            timestamp: new Date(),
            avatar: data.lead?.user?.avatar
        };

        setNotifications(prev => [notification, ...prev.slice(0, 4)]);
        setIsVisible(true);

        setTimeout(() => {
            setIsVisible(false);
        }, 5000);
    };

    const handleNotificationClick = (notification) => {
        if (notification.leadId) {
            // Navigate to chat or lead details
            window.location.href = `/chat/${notification.leadId}`;
        }
        setIsVisible(false);
    };

    const handleClose = () => {
        setIsVisible(false);
    };

    const formatTime = (timestamp) => {
        return dayjs(timestamp).format('HH:mm');
    };

    if (!isVisible || notifications.length === 0) {
        return null;
    }

    const latestNotification = notifications[0];

    return (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm animate-in slide-in-from-bottom-2 duration-300">
            <Card
                className="shadow-2xl border-0 rounded-2xl overflow-hidden backdrop-blur-sm"
                style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                }}
            >
                <div className="flex items-start justify-between p-4">
                    <div className="flex items-start space-x-3 flex-1">
                        <Avatar
                            size={44}
                            icon={<UserOutlined />}
                            src={latestNotification.avatar}
                            className="flex-shrink-0 ring-2 ring-white ring-opacity-30"
                        />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-2">
                                <Title level={5} className="!mb-0 !text-white font-semibold">
                                    {latestNotification.title}
                                </Title>
                                {notifications.length > 1 && (
                                    <Badge 
                                        count={notifications.length} 
                                        size="small"
                                        className="bg-red-500 shadow-lg"
                                    />
                                )}
                            </div>
                            <AntText className="text-white text-sm block truncate font-medium mb-1">
                                {latestNotification.sender}
                            </AntText>
                            <AntText className="text-white text-xs opacity-90 block mb-2 leading-relaxed">
                                {latestNotification.message}
                            </AntText>
                            <AntText className="text-white text-xs opacity-70">
                                {formatTime(latestNotification.timestamp)}
                            </AntText>
                        </div>
                    </div>
                    <div className="flex flex-col space-y-2 ml-3">
                        <Button
                            type="text"
                            icon={<CloseOutlined />}
                            onClick={handleClose}
                            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full w-8 h-8 flex items-center justify-center"
                            size="small"
                        />
                        <Button
                            type="primary"
                            size="small"
                            onClick={() => handleNotificationClick(latestNotification)}
                            className="bg-white text-gray-800 hover:bg-gray-100 rounded-full px-4 py-1 font-medium shadow-lg"
                        >
                            View
                        </Button>
                    </div>
                </div>
            </Card>
        </div>
    );
}

export default MessageNotification;
